import { atom, useAtomValue } from "jotai";
import { type JSX, useEffect, useRef, useState } from "react";

import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from "@frontend/components/atoms/Autocomplete";
import { useLingui } from "@lingui/react/macro";

import { useAddressText } from "../hooks/useAddressText";
import { addressApi } from "../services";
import type { AddressFieldProps, AddressTextField } from "../types";
import type { AddressSuggestion } from "../types";
import { FieldFrame } from "./FieldFrame";

/** How long the field waits before asking the referential again. */
const DEBOUNCE_MS = 300;

/** Stands in for the country field a caller did not name. */
const NO_COUNTRY = atom<string | null | undefined>("");

export type StreetFieldProps = AddressFieldProps & {
  /** Narrows the search to one country, and is read as it changes. */
  countryField?: AddressTextField;
  onAddressSelected?: (address: AddressSuggestion) => void;
};

/**
 * The street, completed from the referential. What is typed stays whatever it
 * is: the suggestions help, they do not constrain.
 */
export function StreetField({
  field,
  countryField,
  label,
  placeholder,
  size,
  disabled,
  onAddressSelected,
}: StreetFieldProps): JSX.Element {
  const { t } = useLingui();

  const { value: street, setValue: setStreet, error } = useAddressText(field);

  const country = useCountry(countryField);

  // What the list is showing, rather than matching the typed string back
  // against a label: two streets of the same name are two addresses.
  const highlighted = useRef<AddressSuggestion | null>(null);

  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);

  const query = street.trim();

  useEffect(() => {
    if ("" === query) {
      return;
    }

    const controller = new AbortController();

    const timeout = setTimeout(() => {
      addressApi
        .searchAddresses(
          { q: query, countryIso2: country || undefined },
          { signal: controller.signal },
        )
        .then(setSuggestions)
        .catch(() => {
          // Aborted or unanswered: the field stays a plain text one.
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [query, country]);

  return (
    <FieldFrame label={label} error={error}>
      <Autocomplete<AddressSuggestion>
        items={"" === query ? [] : suggestions}
        value={street}
        size={size}
        disabled={disabled}
        filter={null}
        itemToStringValue={(item) => item.label}
        onItemHighlighted={(item) => {
          highlighted.current = item ?? null;
        }}
        onValueChange={(next, details) => {
          setStreet(next);

          if ("item-press" === details.reason && highlighted.current) {
            onAddressSelected?.(highlighted.current);
          }
        }}
      >
        <AutocompleteInput placeholder={placeholder ?? t`Street`} />

        <AutocompleteContent>
          <AutocompleteEmpty>{t`No results found.`}</AutocompleteEmpty>

          <AutocompleteList>
            {(item: AddressSuggestion) => (
              <AutocompleteItem key={item.id} value={item}>
                {item.cityName
                  ? `${item.label} (${item.cityName})`
                  : item.label}
              </AutocompleteItem>
            )}
          </AutocompleteList>
        </AutocompleteContent>
      </Autocomplete>
    </FieldFrame>
  );
}

/** The country an address is searched in, when the caller names that field. */
function useCountry(field?: AddressTextField): string {
  return useAtomValue(field?.atom ?? NO_COUNTRY) ?? "";
}
